/**
 * Field Validation Utilities
 *
 * This file contains validation logic for table fields.
 * Used to ensure data integrity before saving changes.
 */

import { FIELD_DATA_TYPES, type FieldDataType } from '@loader/shared-types';

import type { EditableField } from '@/features/schemas/types';

/**
 * Validation result interface
 */
export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Field with data type for validation
 */
interface FieldWithDataType {
  name: string;
  dataType: string;
}

/**
 * Validate that all fields have non-empty names
 * @param fields - Array of fields to validate
 * @returns Validation result with error message if invalid
 */
export const validateFieldNames = (
  fields: EditableField[]
): ValidationResult => {
  const emptyNames = fields.filter((f) => !f.name.trim());
  if (emptyNames.length > 0) {
    return {
      isValid: false,
      error: 'All fields must have a name',
    };
  }
  return { isValid: true };
};

/**
 * Validate that all field names are unique
 * @param fields - Array of fields to validate
 * @returns Validation result with error message if invalid
 */
export const validateUniqueFieldNames = (
  fields: EditableField[]
): ValidationResult => {
  const duplicateNames = fields.filter(
    (f, i) => fields.findIndex((f2) => f2.name === f.name) !== i
  );
  if (duplicateNames.length > 0) {
    return {
      isValid: false,
      error: 'Field names must be unique',
    };
  }
  return { isValid: true };
};

/**
 * Check if a data type is valid
 * @param dataType - Data type to check
 * @returns True if valid, false otherwise
 */
export const isValidDataType = (dataType: string): dataType is FieldDataType => {
  return (FIELD_DATA_TYPES as readonly string[]).includes(dataType);
};

/**
 * Validate that all fields have valid data types
 * @param fields - Array of fields to validate
 * @returns Validation result with error message if invalid
 */
export const validateFieldDataTypes = <T extends FieldWithDataType>(
  fields: T[]
): ValidationResult => {
  const invalidFields = fields.filter((f) => !isValidDataType(f.dataType));
  if (invalidFields.length > 0) {
    const fieldNames = invalidFields.map((f) => `"${f.name}"`).join(', ');
    return {
      isValid: false,
      error: `Invalid data type for field${invalidFields.length > 1 ? 's' : ''}: ${fieldNames}`,
    };
  }
  return { isValid: true };
};

/**
 * Validate all fields before saving
 * Runs all validation checks and returns the first error encountered
 * @param fields - Array of fields to validate
 * @returns Validation result with error message if invalid
 */
export const validateFields = (fields: EditableField[]): ValidationResult => {
  // Check for empty names
  const nameValidation = validateFieldNames(fields);
  if (!nameValidation.isValid) {
    return nameValidation;
  }

  // Check for duplicate names
  const uniqueValidation = validateUniqueFieldNames(fields);
  if (!uniqueValidation.isValid) {
    return uniqueValidation;
  }

  return { isValid: true };
};

/**
 * Validate create table field configuration
 * Runs all validation checks including data type validation
 * @param fields - Array of fields to validate
 * @returns Validation result with error message if invalid
 */
export const validateCreateTableFields = <T extends FieldWithDataType>(
  fields: T[]
): ValidationResult => {
  // Check for empty names
  const fieldsWithEmptyNames = fields.filter((f) => !f.name.trim());
  if (fieldsWithEmptyNames.length > 0) {
    return {
      isValid: false,
      error: 'All fields must have a name',
    };
  }

  // Check for duplicate names
  const duplicateNames = fields.filter(
    (f, i) => fields.findIndex((f2) => f2.name === f.name) !== i
  );
  if (duplicateNames.length > 0) {
    return {
      isValid: false,
      error: 'Field names must be unique',
    };
  }

  // Check for valid data types
  const typeValidation = validateFieldDataTypes(fields);
  if (!typeValidation.isValid) {
    return typeValidation;
  }

  return { isValid: true };
};

